/**
 * Site Attendance List Page - View attendance sessions (Roll-Call Model)
 */

import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  makeStyles,
  Text,
  Button,
  Input,
  Badge,
  tokens,
  Spinner,
} from '@fluentui/react-components';
import {
  SearchRegular,
  PersonRegular,
  CheckmarkCircleRegular,
  EyeRegular,
  DismissRegular,
  ArrowDownloadRegular,
  PersonAvailableRegular,
  PersonProhibitedRegular,
  ClipboardTaskListLtrRegular,
  CalendarRegular,
} from '@fluentui/react-icons';
import { ContentCard } from '../../../components/layout';
import { PermissionGate, SearchableDropdown, TablePagination } from '../../../components/common';
import { usePagination } from '../../../hooks/usePagination';
import { ApiClient, newToast } from '../../../configurations/index.config';
import type { AttendanceSession, AttendanceSummary, Site } from '../../../types';
import { getErrorMessage } from '../../../types';

const useStyles = makeStyles({
  container: {
    padding: '24px',
  },
  pageHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '24px',
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  },
  headerIcon: {
    fontSize: '32px',
    color: tokens.colorBrandForeground1,
  },
  headerText: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  pageTitle: {
    fontSize: '24px',
    fontWeight: '600',
    color: '#323130',
  },
  pageSubtitle: {
    fontSize: '14px',
    color: '#605e5c',
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(5, 1fr)',
    gap: '16px',
    marginBottom: '24px',
  },
  statCard: {
    padding: '20px',
    backgroundColor: '#ffffff',
    borderRadius: '8px',
    border: '1px solid #e1dfdd',
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  },
  statIcon: {
    width: '48px',
    height: '48px',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '24px',
  },
  statContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  statValue: {
    fontSize: '28px',
    fontWeight: '600',
    color: '#323130',
  },
  statLabel: {
    fontSize: '13px',
    color: '#605e5c',
  },
  filterSection: {
    marginBottom: '24px',
  },
  filterGrid: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  filterRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 150px 150px 1fr auto auto',
    gap: '12px',
    alignItems: 'end',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  tableHeader: {
    backgroundColor: '#f5f5f5',
    borderBottom: '2px solid #e1dfdd',
  },
  th: {
    padding: '12px 16px',
    textAlign: 'left',
    fontSize: '13px',
    fontWeight: '600',
    color: '#323130',
  },
  thCenter: {
    padding: '12px 16px',
    textAlign: 'center',
    fontSize: '13px',
    fontWeight: '600',
    color: '#323130',
  },
  tr: {
    borderBottom: '1px solid #e1dfdd',
    ':hover': {
      backgroundColor: '#f9f9f9',
    },
  },
  td: {
    padding: '12px 16px',
    fontSize: '14px',
    color: '#323130',
  },
  tdCenter: {
    padding: '12px 16px',
    fontSize: '14px',
    color: '#323130',
    textAlign: 'center',
  },
  emptyState: {
    padding: '60px 20px',
    textAlign: 'center',
    color: '#605e5c',
  },
  actionButtons: {
    display: 'flex',
    gap: '8px',
  },
  employeeInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  employeeName: {
    fontWeight: '500',
  },
  siteClient: {
    fontSize: '12px',
    color: '#605e5c',
  },
  attendanceBar: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  progressBarContainer: {
    width: '80px',
    height: '8px',
    backgroundColor: '#e1dfdd',
    borderRadius: '4px',
    overflow: 'hidden',
  },
  progressBar: {
    height: '100%',
    borderRadius: '4px',
    transition: 'width 0.3s ease',
  },
  sessionInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  sessionTime: {
    fontSize: '12px',
    color: '#605e5c',
  },
});

export const SiteAttendanceFluentUI: React.FC = () => {
  const styles = useStyles();
  const navigate = useNavigate();

  const [sessions, setSessions] = useState<AttendanceSession[]>([]);
  const [sites, setSites] = useState<Site[]>([]);
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState<AttendanceSummary | null>(null);

  const [filterSite, setFilterSite] = useState('');
  const [filterFromDate, setFilterFromDate] = useState(new Date().toISOString().split('T')[0]);
  const [filterToDate, setFilterToDate] = useState(new Date().toISOString().split('T')[0]);
  const [searchQuery, setSearchQuery] = useState('');

  const filteredSessions = useMemo(() => {
    let filtered = [...sessions];

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        s =>
          s.site_name?.toLowerCase().includes(query) ||
          s.client_name?.toLowerCase().includes(query) ||
          s.conducted_by?.toLowerCase().includes(query)
      );
    }

    return filtered;
  }, [sessions, searchQuery]);

  const { paginatedData, currentPage, totalItems, itemsPerPage, setCurrentPage, setItemsPerPage } =
    usePagination<AttendanceSession>({
      data: filteredSessions,
      initialItemsPerPage: 10,
    });

  const calculateSummary = (data: AttendanceSession[]): AttendanceSummary => {
    if (data.length === 0) {
      return {
        total_sessions: 0,
        total_guards_checked: 0,
        total_present: 0,
        total_absent: 0,
        avg_attendance_percentage: 0,
      };
    }
    return {
      total_sessions: data.length,
      total_guards_checked: data.reduce((sum, s) => sum + (Number(s.total_guards) || 0), 0),
      total_present: data.reduce((sum, s) => sum + (Number(s.present) || 0), 0),
      total_absent: data.reduce((sum, s) => sum + (Number(s.absent) || 0), 0),
      avg_attendance_percentage:
        data.reduce((sum, s) => sum + (Number(s.attendance_percentage) || 0), 0) / data.length,
    };
  };

  useEffect(() => {
    fetchSessions();
    fetchSites();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchSessions = async () => {
    setLoading(true);
    try {
      const params: Record<string, string> = {};
      if (filterSite) {
        params.site_id = filterSite;
      }
      if (filterFromDate) {
        params.from_date = filterFromDate;
      }
      if (filterToDate) {
        params.to_date = filterToDate;
      }

      const response = await ApiClient.get('/get-site-attendance-history', { params });
      if (response.data.status === 'success') {
        const data = response.data.data || [];
        setSessions(data);
        setSummary(calculateSummary(data));
      }
    } catch (error: unknown) {
      newToast('error', getErrorMessage(error));
    }
    setLoading(false);
  };

  const fetchSites = async () => {
    try {
      const response = await ApiClient.get('/get-sites');
      if (response.data.status === 'success') {
        setSites(response.data.data || []);
      }
    } catch (error: unknown) {
      console.error('Error fetching sites:', error);
    }
  };

  const applyFilters = () => {
    fetchSessions();
  };

  const clearFilters = () => {
    setSearchQuery('');
    setFilterSite('');
    setFilterFromDate(new Date().toISOString().split('T')[0]);
    setFilterToDate(new Date().toISOString().split('T')[0]);
    setTimeout(() => {
      fetchSessions();
    }, 0);
  };

  const hasActiveFilters =
    searchQuery !== '' ||
    filterSite !== '' ||
    filterFromDate !== new Date().toISOString().split('T')[0] ||
    filterToDate !== new Date().toISOString().split('T')[0];

  const getSessionStatusBadge = (status: string) => {
    switch (status) {
      case 'completed':
        return (
          <Badge appearance="filled" color="success">
            Completed
          </Badge>
        );
      case 'expired':
        return (
          <Badge appearance="filled" color="warning">
            Expired
          </Badge>
        );
      case 'cancelled':
        return (
          <Badge appearance="filled" color="danger">
            Cancelled
          </Badge>
        );
      default:
        return (
          <Badge appearance="filled" color="subtle">
            {status}
          </Badge>
        );
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const formatTime = (timeString: string) => {
    if (!timeString) {
      return '—';
    }
    if (timeString.includes('T')) {
      const date = new Date(timeString);
      return date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      });
    }
    return timeString;
  };

  const getAttendanceColor = (percentage: number) => {
    if (percentage >= 80) {
      return '#107c10';
    }
    if (percentage >= 50) {
      return '#ffaa44';
    }
    return '#d13438';
  };

  const handleViewSession = (session: AttendanceSession) => {
    navigate(`/account/security-operations/site-attendance/${session.session_id}`);
  };

  return (
    <PermissionGate privilege="get-site-attendance-history">
      <div className={styles.container}>
        {/* Page Header */}
        <div className={styles.pageHeader}>
          <div className={styles.headerLeft}>
            <PersonAvailableRegular className={styles.headerIcon} />
            <div className={styles.headerText}>
              <Text className={styles.pageTitle}>Site Attendance</Text>
              <Text className={styles.pageSubtitle}>
                View attendance roll-call sessions across all sites
              </Text>
            </div>
          </div>
          <Button
            appearance="subtle"
            icon={<ArrowDownloadRegular />}
            onClick={() => newToast('info', 'Export feature coming soon')}
          >
            Export
          </Button>
        </div>

        {/* Stats Cards */}
        <div className={styles.statsGrid}>
          <div className={styles.statCard}>
            <div
              className={styles.statIcon}
              style={{ backgroundColor: '#e8f4fd', color: '#0078d4' }}
            >
              <ClipboardTaskListLtrRegular />
            </div>
            <div className={styles.statContent}>
              <Text className={styles.statValue}>{summary?.total_sessions ?? 0}</Text>
              <Text className={styles.statLabel}>Total Sessions</Text>
            </div>
          </div>

          <div className={styles.statCard}>
            <div
              className={styles.statIcon}
              style={{ backgroundColor: '#f3e8fd', color: '#8764b8' }}
            >
              <PersonRegular />
            </div>
            <div className={styles.statContent}>
              <Text className={styles.statValue}>{summary?.total_guards_checked ?? 0}</Text>
              <Text className={styles.statLabel}>Guards Checked</Text>
            </div>
          </div>

          <div className={styles.statCard}>
            <div
              className={styles.statIcon}
              style={{ backgroundColor: '#dff6dd', color: '#107c10' }}
            >
              <CheckmarkCircleRegular />
            </div>
            <div className={styles.statContent}>
              <Text className={styles.statValue}>{summary?.total_present ?? 0}</Text>
              <Text className={styles.statLabel}>Present</Text>
            </div>
          </div>

          <div className={styles.statCard}>
            <div
              className={styles.statIcon}
              style={{ backgroundColor: '#fde7e9', color: '#d13438' }}
            >
              <PersonProhibitedRegular />
            </div>
            <div className={styles.statContent}>
              <Text className={styles.statValue}>{summary?.total_absent ?? 0}</Text>
              <Text className={styles.statLabel}>Absent</Text>
            </div>
          </div>

          <div className={styles.statCard}>
            <div
              className={styles.statIcon}
              style={{ backgroundColor: '#fff4ce', color: '#ffaa44' }}
            >
              <CalendarRegular />
            </div>
            <div className={styles.statContent}>
              <Text className={styles.statValue}>
                {summary?.avg_attendance_percentage?.toFixed(1) ?? 0}%
              </Text>
              <Text className={styles.statLabel}>Avg Attendance</Text>
            </div>
          </div>
        </div>

        {/* Filters Section */}
        <ContentCard>
          <div className={styles.filterSection}>
            <div className={styles.filterGrid}>
              <div className={styles.filterRow}>
                <SearchableDropdown
                  placeholder="All Sites"
                  options={[
                    { id: '', label: 'All Sites' },
                    ...sites.map(s => ({
                      id: s.id.toString(),
                      label: s.site_name || s.name || 'Unknown Site',
                    })),
                  ]}
                  selectedValue={filterSite}
                  onSelect={setFilterSite}
                />

                <Input
                  type="date"
                  value={filterFromDate}
                  onChange={(e, data) => setFilterFromDate(data.value)}
                />

                <Input
                  type="date"
                  value={filterToDate}
                  onChange={(e, data) => setFilterToDate(data.value)}
                />

                <Input
                  placeholder="Search sites, clients, admins..."
                  value={searchQuery}
                  onChange={(e, data) => setSearchQuery(data.value)}
                  contentBefore={<SearchRegular />}
                />

                <Button appearance="primary" onClick={applyFilters}>
                  Apply
                </Button>

                {hasActiveFilters && (
                  <Button appearance="subtle" icon={<DismissRegular />} onClick={clearFilters}>
                    Clear
                  </Button>
                )}
              </div>
            </div>
          </div>
        </ContentCard>

        {/* Sessions Table */}
        <ContentCard noPadding>
          {loading ? (
            <div className={styles.emptyState}>
              <Spinner size="medium" label="Loading attendance sessions..." />
            </div>
          ) : filteredSessions.length === 0 ? (
            <div className={styles.emptyState}>
              <Text size={500} weight="semibold" style={{ display: 'block' }}>
                No attendance sessions found
              </Text>
              <Text style={{ marginTop: '8px', display: 'block' }}>
                {hasActiveFilters
                  ? 'Try adjusting your filters or search query'
                  : 'No attendance sessions for the selected date range'}
              </Text>
            </div>
          ) : (
            <table className={styles.table}>
              <thead className={styles.tableHeader}>
                <tr>
                  <th className={styles.th}>Site</th>
                  <th className={styles.th}>Client</th>
                  <th className={styles.th}>Conducted By</th>
                  <th className={styles.thCenter}>Date</th>
                  <th className={styles.thCenter}>Time</th>
                  <th className={styles.thCenter}>Present / Total</th>
                  <th className={styles.thCenter}>Attendance</th>
                  <th className={styles.thCenter}>Status</th>
                  <th className={styles.th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedData.map(session => (
                  <tr key={session.session_id} className={styles.tr}>
                    <td className={styles.td}>
                      <Text className={styles.employeeName}>{session.site_name}</Text>
                    </td>
                    <td className={styles.td}>
                      <Text>{session.client_name}</Text>
                    </td>
                    <td className={styles.td}>
                      <Text>{session.conducted_by}</Text>
                    </td>
                    <td className={styles.tdCenter}>{formatDate(session.session_date)}</td>
                    <td className={styles.tdCenter}>
                      <div className={styles.sessionInfo}>
                        <Text>{formatTime(session.session_start)}</Text>
                        <Text className={styles.sessionTime}>
                          to {formatTime(session.session_end)}
                        </Text>
                      </div>
                    </td>
                    <td className={styles.tdCenter}>
                      <Text>
                        <strong>{session.present}</strong> / {session.total_guards}
                      </Text>
                    </td>
                    <td className={styles.tdCenter}>
                      <div className={styles.attendanceBar}>
                        <div className={styles.progressBarContainer}>
                          <div
                            className={styles.progressBar}
                            style={{
                              width: `${Number(session.attendance_percentage) || 0}%`,
                              backgroundColor: getAttendanceColor(
                                Number(session.attendance_percentage) || 0
                              ),
                            }}
                          />
                        </div>
                        <Text style={{ fontSize: '13px' }}>
                          {(Number(session.attendance_percentage) || 0).toFixed(0)}%
                        </Text>
                      </div>
                    </td>
                    <td className={styles.tdCenter}>{getSessionStatusBadge(session.status)}</td>
                    <td className={styles.td}>
                      <div className={styles.actionButtons}>
                        <Button
                          size="small"
                          appearance="subtle"
                          icon={<EyeRegular />}
                          onClick={() => handleViewSession(session)}
                        >
                          View
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {!loading && filteredSessions.length > 0 && (
            <TablePagination
              totalItems={totalItems}
              currentPage={currentPage}
              itemsPerPage={itemsPerPage}
              onPageChange={setCurrentPage}
              onItemsPerPageChange={setItemsPerPage}
            />
          )}
        </ContentCard>
      </div>
    </PermissionGate>
  );
};

export default SiteAttendanceFluentUI;
